import React, { useState, useEffect } from 'react'
import * as api from '../../../app/axios/api.jsx'
import { Trash2, X, Plus } from 'lucide-react'
import { toast } from 'sonner'

const Page = () => {
  const [data, setData] = useState([])
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    payment_mode: '',
    paid_for: '',
    date: '',
    remark: '',
  })
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [showCreateFormModal, setShowCreateFormModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [collectionToDelete, setCollectionToDelete] = useState(null)

  const createCollection = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      // Convert date to ISO format with time
      const dateTimeISO = new Date(formData.date + 'T00:00:00').toISOString()

      const newCollection = {
        name: formData.name,
        amount: parseInt(formData.amount), // Convert to integer
        payment_mode: formData.payment_mode,
        paid_for: formData.paid_for,
        date: dateTimeISO,
        remark: formData.remark,
      }

      const response = await api.createCollection(newCollection)

      toast.success('Collection created successfully!')

      // Reset form
      setFormData({
        name: '',
        amount: '',
        payment_mode: '',
        paid_for: '',
        date: '',
        remark: '',
      })
      setShowCreateFormModal(false)
      fetchData()
    } catch (error) {
      console.error('Error creating collection:', error)
      toast.error('Error creating collection. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const fetchData = async () => {
    setFetchLoading(true)
    try {
      const res = await api.fetchCollection()
      setData(res.data)
    } catch (error) {
      console.error('Error fetching collection data:', error)
    } finally {
      setFetchLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleDeleteClick = (item) => {
    setCollectionToDelete(item)
    setShowDeleteConfirmModal(true)
  }

  const confirmDelete = async () => {
    if (!collectionToDelete) return

    setLoading(true)
    try {
      const response = await api.deleteCollection(collectionToDelete.id)

      toast.success(
        `Collection "${collectionToDelete.name}" deleted successfully!`,
      )

      setShowDeleteConfirmModal(false)
      setCollectionToDelete(null)
      fetchData()
    } catch (error) {
      console.error('Error deleting collection:', error)
      toast.error('Error deleting collection. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      return 'N/A'
    }
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4 md:p-6'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='bg-white rounded-lg shadow-lg p-3 sm:p-4 md:p-6 mb-4 md:mb-6'>
          <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4'>
            <div>
              <h1 className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-800'>
                Collection Management
              </h1>
              <p className='text-sm sm:text-base text-gray-600 mt-1'>
                Create and manage your collections
              </p>
            </div>
            <button
              onClick={() => setShowCreateFormModal(true)}
              className='w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition shadow-lg flex items-center justify-center gap-2'
            >
              <Plus size={20} />
              Create Collection
            </button>
          </div>
        </div>

        {/* Create Form Modal */}
        {showCreateFormModal && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
              <div className='sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center'>
                <h2 className='text-xl sm:text-2xl font-semibold text-gray-800'>
                  Create New Collection
                </h2>
                <button
                  onClick={() => setShowCreateFormModal(false)}
                  className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                >
                  <X size={24} className='text-gray-600' />
                </button>
              </div>

              <form onSubmit={createCollection} className='p-4 sm:p-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {/* Name */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Name *
                    </label>
                    <input
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='Enter name'
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Amount *
                    </label>
                    <input
                      type='number'
                      name='amount'
                      value={formData.amount}
                      onChange={handleChange}
                      required
                      min='0'
                      step='1'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='Enter amount'
                    />
                  </div>

                  {/* Payment Mode */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Payment Mode *
                    </label>
                    <select
                      name='payment_mode'
                      value={formData.payment_mode}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                    >
                      <option value=''>Select payment mode</option>
                      <option value='cash'>Cash</option>
                      <option value='upi'>UPI</option>
                    </select>
                  </div>

                  {/* Paid For */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Paid For *
                    </label>
                    <input
                      type='text'
                      name='paid_for'
                      value={formData.paid_for}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='What is this payment for?'
                    />
                  </div>

                  {/* Date */}
                  <div className='sm:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Date *
                    </label>
                    <input
                      type='date'
                      name='date'
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                    />
                  </div>

                  {/* Remark */}
                  <div className='sm:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Remark
                    </label>
                    <textarea
                      name='remark'
                      value={formData.remark}
                      onChange={handleChange}
                      rows='3'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none'
                      placeholder='Add any additional notes...'
                    />
                  </div>
                </div>

                <button
                  type='submit'
                  disabled={loading}
                  className='w-full mt-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg'
                >
                  {loading ? 'Creating...' : 'Create Collection'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Excel-style Table */}
        <div className='bg-white rounded-lg shadow-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='bg-gradient-to-r from-indigo-600 to-purple-600 text-white'>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Name
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Amount (₹)
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Payment Mode
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Paid For
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Date
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Remark
                  </th>
                  <th className='px-3 py-3 text-center text-xs sm:text-sm font-semibold border border-gray-300'>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {fetchLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className='px-3 py-8 text-center text-gray-600 border border-gray-300'
                    >
                      Loading collections...
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className='px-3 py-8 text-center text-gray-600 border border-gray-300'
                    >
                      No collections found. Start by creating one!
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr
                      key={item.id}
                      className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300 font-medium'>
                        {item.name}
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm font-semibold text-green-600 border border-gray-300'>
                        ₹
                        {item.amount
                          ? item.amount.toLocaleString('en-IN')
                          : '0'}
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm border border-gray-300'>
                        <span className='px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium capitalize'>
                          {item.payment_mode || 'N/A'}
                        </span>
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300'>
                        {item.paid_for || 'N/A'}
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300 whitespace-nowrap'>
                        {formatDate(item.created_at)}
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300 max-w-xs'>
                        <div
                          className='truncate'
                          title={item.remark || 'No remark'}
                        >
                          {item.remark || 'No remark'}
                        </div>
                      </td>
                      <td className='px-3 py-3 text-center border border-gray-300'>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className='px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors flex items-center gap-1 mx-auto'
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmModal && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-2xl max-w-md w-full p-6'>
              <div className='flex items-center gap-3 mb-4'>
                <div className='p-3 bg-red-100 rounded-full'>
                  <Trash2 size={24} className='text-red-600' />
                </div>
                <h3 className='text-xl font-semibold text-gray-800'>
                  Confirm Delete
                </h3>
              </div>
              <p className='text-gray-600 mb-6'>
                Are you sure you want to delete the collection{' '}
                <span className='font-bold'>"{collectionToDelete?.name}"</span>?
                This action cannot be undone.
              </p>
              <div className='flex gap-3 justify-end'>
                <button
                  onClick={() => setShowDeleteConfirmModal(false)}
                  className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className='px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                  disabled={loading}
                >
                  <Trash2 size={16} />
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Page
